<p class="card-chart__title" *ngIf="title">{{ title }}</p>

<!-- BARS — ranked horizontal bar chart -->
<div class="card-bars" *ngIf="variant === 'bars'">
  <div class="card-bars__row" *ngFor="let row of barRows(items)">
    <span class="card-bars__label">{{ row.item.label }}</span>
    <div class="card-bars__track" role="presentation">
      <div class="card-bars__fill" [style.width.%]="row.pct" aria-hidden="true"></div>
    </div>
    <span class="card-bars__display">{{ displayValue(row.item, unit) }}</span>
  </div>
</div>

<!-- TREND — sparkline over ordered periods (optional dashed comparison line) -->
<div class="card-trend" *ngIf="variant === 'trend'">
  <div
    class="card-trend__legend"
    *ngIf="hasSeries2(items) && seriesLabels?.length"
    aria-hidden="true">
    <span class="card-trend__chip card-trend__chip--s1">{{ groupedLabel(0) }}</span>
    <span class="card-trend__chip card-trend__chip--s2">{{ groupedLabel(1) }}</span>
  </div>
  <svg
    class="card-trend__svg"
    width="100%"
    [attr.height]="sparkH"
    [attr.viewBox]="'0 0 ' + sparkW + ' ' + sparkH"
    preserveAspectRatio="none"
    [attr.aria-label]="sparkAriaLabel(title, items)"
    role="img">
    <ng-container *ngIf="sparkPoints(items) as pts">
      <polygon
        *ngIf="pts.length > 1"
        [attr.points]="sparkAreaPolygon(pts)"
        fill="color-mix(in srgb, var(--nova-accent) 12%, transparent)"
        stroke="none"/>
      <polyline
        *ngIf="pts.length > 1"
        [attr.points]="sparkPolyline(pts)"
        fill="none"
        stroke="var(--nova-accent)"
        stroke-width="2"
        stroke-linejoin="round"
        stroke-linecap="round"/>
      <circle
        *ngIf="pts.length"
        [attr.cx]="pts[pts.length - 1].x"
        [attr.cy]="pts[pts.length - 1].y"
        r="3.5"
        fill="var(--nova-accent)"/>
    </ng-container>
    <ng-container *ngIf="sparkPoints2(items) as p2">
      <polyline
        *ngIf="p2.length > 1"
        [attr.points]="sparkPolyline(p2)"
        fill="none"
        stroke="var(--nova-accent-secondary)"
        stroke-width="2"
        stroke-dasharray="4 3"
        stroke-linejoin="round"
        stroke-linecap="round"/>
      <circle
        *ngIf="p2.length"
        [attr.cx]="p2[p2.length - 1].x"
        [attr.cy]="p2[p2.length - 1].y"
        r="3.5"
        fill="var(--nova-accent-secondary)"/>
    </ng-container>
  </svg>
</div>

<!-- GROUPED — two-series horizontal bar chart with legend -->
<div
  class="card-grouped"
  *ngIf="variant === 'grouped'"
  [attr.aria-label]="(title ?? 'Grouped bars') + ': ' + groupedLabel(0) + ' vs ' + groupedLabel(1)">
  <div class="card-grouped__legend" aria-hidden="true">
    <span class="card-grouped__chip card-grouped__chip--s1">{{ groupedLabel(0) }}</span>
    <span class="card-grouped__chip card-grouped__chip--s2">{{ groupedLabel(1) }}</span>
  </div>
  <div class="card-grouped__row" *ngFor="let row of groupedRows(items)">
    <span class="card-grouped__label">{{ row.item.label }}</span>
    <div class="card-grouped__bars">
      <!-- Series 1 -->
      <div class="card-grouped__bar-wrap">
        <div class="card-grouped__track" role="presentation">
          <div
            class="card-grouped__fill card-grouped__fill--s1"
            [style.width.%]="row.pct1"
            aria-hidden="true"></div>
        </div>
        <span class="card-grouped__value">{{ displayValue(row.item, unit) }}</span>
      </div>
      <!-- Series 2 (only when value2 is present) -->
      <div class="card-grouped__bar-wrap" *ngIf="row.item.value2 != null">
        <div class="card-grouped__track" role="presentation">
          <div
            class="card-grouped__fill card-grouped__fill--s2"
            [style.width.%]="row.pct2"
            aria-hidden="true"></div>
        </div>
        <span class="card-grouped__value">{{ row.item.value2 }}</span>
      </div>
    </div>
  </div>
</div>

<!-- DIVERGING — signed bars from a center axis -->
<div
  class="card-diverging"
  *ngIf="variant === 'diverging'"
  [attr.aria-label]="(title ?? 'Diverging chart') + ': top movers'">
  <div class="card-diverging__row" *ngFor="let row of divergingRows(items)">
    <span class="card-diverging__label">{{ row.item.label }}</span>
    <div class="card-diverging__axis" role="presentation">
      <!-- Negative bar grows leftward -->
      <div class="card-diverging__half card-diverging__half--neg">
        <div
          class="card-diverging__fill card-diverging__fill--neg"
          [style.width.%]="row.pctNeg"
          aria-hidden="true"></div>
      </div>
      <!-- Positive bar grows rightward -->
      <div class="card-diverging__half card-diverging__half--pos">
        <div
          class="card-diverging__fill card-diverging__fill--pos"
          [style.width.%]="row.pctPos"
          aria-hidden="true"></div>
      </div>
    </div>
    <span
      class="card-diverging__display"
      [class.card-diverging__display--pos]="row.positive"
      [class.card-diverging__display--neg]="!row.positive">
      {{ row.display }}
    </span>
  </div>
</div>

<!-- DONUT — share/composition donut ring -->
<div
  class="card-donut"
  *ngIf="variant === 'donut'"
  [attr.aria-label]="donutAriaLabel(title, items)"
  role="img">
  <div class="card-donut__ring-wrap">
    <svg
      class="card-donut__svg"
      [attr.viewBox]="donutViewBox"
      [attr.width]="donutCx * 2"
      [attr.height]="donutCy * 2"
      aria-hidden="true">
      <!-- Track circle -->
      <circle
        [attr.cx]="donutCx"
        [attr.cy]="donutCy"
        [attr.r]="donutR"
        fill="none"
        stroke="var(--nova-border)"
        stroke-width="8"/>
      <!-- One arc segment per item, rotated to start at top -->
      <circle
        *ngFor="let seg of donutSegments(items)"
        [attr.cx]="donutCx"
        [attr.cy]="donutCy"
        [attr.r]="donutR"
        fill="none"
        [attr.stroke]="seg.color"
        stroke-width="8"
        stroke-linecap="butt"
        [attr.stroke-dasharray]="seg.dasharray"
        [attr.stroke-dashoffset]="seg.dashoffset"
        transform="rotate(-90 40 40)"/>
    </svg>
    <div class="card-donut__center" aria-hidden="true">
      <span class="card-donut__total">{{ donutTotal(items) }}</span>
    </div>
  </div>
  <ul class="card-donut__legend" aria-hidden="true">
    <li class="card-donut__legend-item" *ngFor="let seg of donutSegments(items)">
      <span class="card-donut__dot" [style.background]="seg.color"></span>
      <span class="card-donut__legend-label">{{ seg.item.label }}</span>
      <span class="card-donut__legend-value">{{ donutItemDisplay(seg.item) }}</span>
      <span class="card-donut__legend-pct">{{ seg.pct }}%</span>
    </li>
  </ul>
</div>

<!-- BULLET — actual bar measured against a target marker -->
<div
  class="card-bullet"
  *ngIf="variant === 'bullet'"
  [attr.aria-label]="(title ?? 'Attainment') + ' versus target'">
  <div class="card-bullet__row" *ngFor="let row of bulletRows(items)">
    <span class="card-bullet__label">{{ row.item.label }}</span>
    <div class="card-bullet__track" role="presentation">
      <div
        class="card-bullet__fill"
        [class.card-bullet__fill--met]="row.met"
        [style.width.%]="row.pct"
        aria-hidden="true"></div>
      <div
        class="card-bullet__target"
        *ngIf="row.targetPct != null"
        [style.left.%]="row.targetPct"
        aria-hidden="true"></div>
    </div>
    <span class="card-bullet__display">
      {{ displayValue(row.item, unit) }}
      <span
        class="card-bullet__attain"
        *ngIf="row.attainment"
        [class.card-bullet__attain--met]="row.met">{{ row.attainment }}</span>
    </span>
  </div>
</div>
